// import axios from "axios";
// import Cookies from "js-cookie";

// const API_BASE_URL = process.env.REACT_APP_API || "";

// const api = axios.create({
//   baseURL: API_BASE_URL,
//   withCredentials: true,
//   headers: {
//     "Content-Type": "application/json",
//   },
// });

// // ✅ ADD TOKEN AUTOMATICALLY
// api.interceptors.request.use(
//   (config) => {
//     const token = Cookies.get("AUTH_TOKEN");
//     if (token) {
//       config.headers.Authorization = `Bearer ${token}`;
//     }
//     return config;
//   },
//   (error) => Promise.reject(error)
// );

// // ✅ HANDLE TOKEN EXPIRY
// api.interceptors.response.use(
//   (response) => response,
//   async (error) => {
//     const originalRequest = error.config;

//     if (error.response?.status === 401 && !originalRequest._retry) {
//       originalRequest._retry = true;
//       try {
//         await axios.post(
//           `${API_BASE_URL}/auth/renew-token`,
//           {},
//           { withCredentials: true }
//         );
//         return api(originalRequest); // 🔁 retry
//       } catch (e) {
//         Cookies.remove("AUTH_TOKEN");
//         window.location.href = "/login";
//         return Promise.reject(e);
//       }
//     }

//     return Promise.reject(error);
//   }
// );

// export default api;

// above is with cookie

// below is with out cookie

import axios from "axios";
import Cookies from "js-cookie";

const API_BASE_URL = process.env.REACT_APP_API || "";

// Must match backend values:
//   jwt.expiration              = 600  seconds → 10 min  (AUTH_TOKEN)
//   jwt.refreshTokenExpiration  = 1800 seconds → 30 min  (REFRESH_TOKEN)
const AUTH_TOKEN_EXPIRY_MS = 10 * 60 * 1000; // 10 minutes
const REFRESH_TOKEN_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

// ── HELPERS ──────────────────────────────────────────────────────────────────

// Read a cookie and strip JSON.stringify quotes if present
const getCookie = (name) => {
  const raw = Cookies.get(name);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw; // already a plain string
  }
};

// Save AUTH_TOKEN cookie — 10 min expiry (matches backend jwt.expiration)
const setAuthTokenCookie = (value) => {
  Cookies.set("AUTH_TOKEN", JSON.stringify(value), {
    expires: new Date(Date.now() + AUTH_TOKEN_EXPIRY_MS),
  });
};

// Save REFRESH_TOKEN cookie — 30 min expiry (matches backend jwt.refreshTokenExpiration)
const setRefreshTokenCookie = (value) => {
  Cookies.set("REFRESH_TOKEN", JSON.stringify(value), {
    expires: new Date(Date.now() + REFRESH_TOKEN_EXPIRY_MS),
  });
};

// Save user_details cookie — tie its expiry to the refresh token (30 min)
// so the user isn't shown as "logged out" while the refresh token is still alive
const setUserDetailsCookie = (value) => {
  Cookies.set("user_details", JSON.stringify(value), {
    expires: new Date(Date.now() + REFRESH_TOKEN_EXPIRY_MS),
  });
};

// Clear all session cookies and redirect to login
const clearSessionAndRedirect = () => {
  Cookies.remove("AUTH_TOKEN");
  Cookies.remove("REFRESH_TOKEN");
  Cookies.remove("user_details");
  window.location.href = "/login";
};

// ── AXIOS INSTANCE ────────────────────────────────────────────────────────────

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// ── REQUEST INTERCEPTOR ───────────────────────────────────────────────────────
// Attach AUTH_TOKEN and REFRESH_TOKEN to every outgoing request automatically.
// The backend reads Authorization header for auth and REFRESH_TOKEN header
// wherever needed (e.g. /auth/renew-token).
api.interceptors.request.use(
  (config) => {
    const accessToken = getCookie("AUTH_TOKEN");
    const refreshToken = getCookie("REFRESH_TOKEN");

    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
      config.headers["AUTH_TOKEN"] = accessToken;
    }

    if (refreshToken) {
      config.headers["REFRESH_TOKEN"] = refreshToken;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ── RESPONSE INTERCEPTOR ──────────────────────────────────────────────────────
// On 401 (AUTH_TOKEN expired):
//   1. Take the REFRESH_TOKEN from the cookie (still valid for up to 30 min)
//   2. Call /api/auth/renew-token — backend validates, returns new both tokens
//   3. Save new tokens with correct expiry times
//   4. Retry the original request transparently — user sees nothing
//
// On renew failure (REFRESH_TOKEN also expired or revoked):
//   → Clear all cookies and redirect to /login
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true; // prevent infinite retry loop

      const refreshToken = getCookie("REFRESH_TOKEN");

      if (!refreshToken) {
        // REFRESH_TOKEN cookie already gone — session fully expired
        clearSessionAndRedirect();
        return Promise.reject(error);
      }

      try {
        // POST to /api/auth/renew-token with the refresh token in the header.
        // Backend (AuthController.renewToken) reads it via:
        //   jwtUtil.getRefreshTokenFromHeader(request) → request.getHeader("REFRESH_TOKEN")
        // Response shape (AuthenticationService.renewToken):
        //   { accessToken, refreshToken, userDetails }
        const renewResponse = await axios.post(
          `${API_BASE_URL}/api/auth/renew-token`,
          {},
          {
            headers: {
              REFRESH_TOKEN: refreshToken,
            },
          },
        );

        const {
          accessToken: newAccessToken,
          refreshToken: newRefreshToken,
          userDetails: newUserDetails,
        } = renewResponse.data;

        // Save new AUTH_TOKEN — 10 min (matches backend jwt.expiration = 600s)
        setAuthTokenCookie(newAccessToken);

        // Save new REFRESH_TOKEN — 30 min (matches backend jwt.refreshTokenExpiration = 1800s)
        // Backend always rotates the refresh token on renew (generateRefreshToken is always called)
        // so newRefreshToken will always be present. The fallback keeps the old one just in case.
        if (newRefreshToken) {
          setRefreshTokenCookie(newRefreshToken);
        } else {
          setRefreshTokenCookie(refreshToken); // fallback — reuse old token, extend cookie expiry
        }

        // Update user_details if backend sends fresh session data
        if (newUserDetails) {
          setUserDetailsCookie(newUserDetails);
        }

        // Patch the Authorization header on the original failed request and retry it
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        originalRequest.headers["AUTH_TOKEN"] = newAccessToken;

        return api(originalRequest); // 🔁 transparent retry
      } catch (renewError) {
        // REFRESH_TOKEN was rejected by backend (expired, revoked, or blacklisted)
        clearSessionAndRedirect();
        return Promise.reject(renewError);
      }
    }

    return Promise.reject(error);
  },
);

export const publicRequest = (method, url, data = null) => {
  return axios({
    method,
    url,
    data,
    headers: { "Content-Type": "application/json" },
  });
};

export default api;

// import axios from "axios";
// import Cookies from "js-cookie";

// const API_BASE_URL = process.env.REACT_APP_API || "";
// const SESSION_DURATION_MS = 10 * 60 * 1000; // 10 minutes — must match login.js and useSessionRefresh.js

// // Helper: read a cookie and strip JSON.stringify quotes if present
// const getCookie = (name) => {
//   const raw = Cookies.get(name);
//   if (!raw) return null;
//   try {
//     return JSON.parse(raw);
//   } catch {
//     return raw;
//   }
// };

// // Helper: save a token cookie with a fresh 10-min expiry
// const setTokenCookie = (name, value) => {
//   const expirationTime = new Date(Date.now() + SESSION_DURATION_MS);
//   Cookies.set(name, JSON.stringify(value), { expires: expirationTime });
// };

// // Helper: clear all session cookies and redirect to login
// const clearSessionAndRedirect = () => {
//   Cookies.remove("AUTH_TOKEN");
//   Cookies.remove("REFRESH_TOKEN");
//   Cookies.remove("user_details");
//   window.location.href = "/login";
// };

// const api = axios.create({
//   baseURL: API_BASE_URL,
//   headers: {
//     "Content-Type": "application/json",
//   },
// });

// // ── REQUEST INTERCEPTOR ──────────────────────────────────────────────────────
// // Attach AUTH_TOKEN and REFRESH_TOKEN to every outgoing request automatically.
// api.interceptors.request.use(
//   (config) => {
//     const accessToken = getCookie("AUTH_TOKEN");
//     const refreshToken = getCookie("REFRESH_TOKEN");

//     if (accessToken) {
//       config.headers["Authorization"] = `Bearer ${accessToken}`;
//       config.headers["AUTH_TOKEN"] = accessToken;
//     }

//     if (refreshToken) {
//       config.headers["REFRESH_TOKEN"] = refreshToken;
//     }

//     return config;
//   },
//   (error) => Promise.reject(error)
// );

// // ── RESPONSE INTERCEPTOR ─────────────────────────────────────────────────────
// // On 401: send the refresh token to /auth/renew-token, save the new tokens
// // returned by the backend, then automatically retry the original request.
// // On renew failure (refresh token also expired): clear everything → /login.
// api.interceptors.response.use(
//   (response) => response,
//   async (error) => {
//     const originalRequest = error.config;

//     if (error.response?.status === 401 && !originalRequest._retry) {
//       originalRequest._retry = true; // prevent infinite retry loop

//       const refreshToken = getCookie("REFRESH_TOKEN");

//       if (!refreshToken) {
//         // No refresh token available — session is fully expired
//         clearSessionAndRedirect();
//         return Promise.reject(error);
//       }

//       try {
//         // Send the refresh token so the backend can validate it and issue
//         // a new access token (and optionally a new refresh token).
//         const renewResponse = await axios.post(
//           `${API_BASE_URL}/api/auth/renew-token`,
//           {},
//           {
//             headers: {
//               "REFRESH_TOKEN": refreshToken,
//             },
//           }
//         );

//         const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
//           renewResponse.data;

//         // Save new tokens with a fresh 10-min expiry
//         setTokenCookie("AUTH_TOKEN", newAccessToken);

//         // Backend may or may not rotate the refresh token — handle both cases
//         if (newRefreshToken) {
//           setTokenCookie("REFRESH_TOKEN", newRefreshToken);
//         } else {
//           // Refresh token not rotated — just extend its cookie expiry
//           setTokenCookie("REFRESH_TOKEN", refreshToken);
//         }

//         // Update the Authorization header on the original request and retry
//         originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
//         originalRequest.headers["AUTH_TOKEN"] = newAccessToken;

//         return api(originalRequest);
//       } catch (renewError) {
//         // Refresh token was rejected by the backend — force re-login
//         clearSessionAndRedirect();
//         return Promise.reject(renewError);
//       }
//     }

//     return Promise.reject(error);
//   }
// );

// export default api;
